<script setup lang="ts">
import { useTranslation } from 'i18next-vue'
import { computed } from 'vue'
import type { DensityColorLabel } from '@/composables/Appointment/useAppointmentCalendar'
import { pickTranslated } from '@/utils/i18nContent'
import { useAppStore } from '@/stores/AppStore'

interface TimeSlot {
  start: string
  end: string
  active: boolean
  density_color?: string | null
}

interface Props {
  hasDate: boolean
  loading: boolean
  timeSlots: TimeSlot[]
  selectedTimeIndex: number[]
  densityColorLabel?: DensityColorLabel[]
}

interface Emits {
  (e: 'selectTime', index: number): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { t } = useTranslation()
const appStore = useAppStore()

// คำอธิบายระดับสีความหนาแน่นตามภาษาที่ลูกค้าเลือก (fallback cn -> en -> th เหมือน i18next.fallbackLng)
function densityLabelText(item: DensityColorLabel): string {
  return pickTranslated(item.description, item.description_en, item.description_cn, appStore.language)
}

const densityColorLabelList = computed(() => {
  // list คำอธิบายสีความหนาแน่นของนัดหมายทั้งหมดที่ถูกแสดงใน time slot
  let densityColorLabelList: DensityColorLabel[] = props.densityColorLabel ?? []
  const usedColors = props.timeSlots.filter(s => !!s.density_color).map(s => s.density_color) as string[]
  return densityColorLabelList.filter(item => usedColors.includes(item.color))
})
const showDensityColorLabel = computed(
  // flag สำหรับ check ว่าต้องแสดงคำอธิบายสีความหนาแน่นของนัดหมายหรือไม่
  // (check ดูว่าใน time slot มี slot ที่มีข้อมูลสีแสดงความหนาแน่นของนัดหมายอยู่หรือไม่)
  () => props.timeSlots.some(s => !!s.density_color) && densityColorLabelList.value.length > 0,
)
</script>

<template>
  <van-cell-group v-if="hasDate" class="group-container" inset>
    <p style="font-size: 18px; font-weight: bold; padding: 15px">
      {{ t('appointmentrequest.select_time') }}
    </p>

    <template v-if="loading">
      <van-loading style="text-align: center; padding-bottom: 25px" size="40" />
    </template>

    <template v-else-if="timeSlots.length === 0">
      <p style="text-align: center; padding: 0px 20px 25px">
        {{ t('appointmentrequest.no_time_slot') }}
      </p>
    </template>

    <template v-else>
      <van-space wrap style="padding: 0px 20px">
        <div v-for="(slot, i) in timeSlots" :key="i">
          <van-button
            size="small"
            v-bind="{
              color: selectedTimeIndex.includes(i) ? '#1989fa' : '',
              disabled: !slot.active,
            }"
            @click.prevent="emit('selectTime', i)"
          >
            <span class="tw-inline-flex tw-items-center tw-gap-1.5">
              <span
                v-if="slot.density_color"
                class="tw-inline-block tw-h-2 tw-w-2 tw-rounded-full"
                :style="{ backgroundColor: slot.density_color }"
              />
              {{ slot.start }}
            </span>
          </van-button>
        </div>
      </van-space>

      <div v-if="showDensityColorLabel" class="tw-px-5 tw-pt-2 tw-pb-[18px] tw-flex tw-flex-col tw-gap-2">
        <div
          v-for="item in densityColorLabelList"
          :key="`density-color-label-${item.color}`"
          class="tw-rounded-[10px] tw-bg-[#eaf6ff] tw-px-3 tw-py-2 tw-flex tw-items-center tw-gap-2"
        >
          <span class="tw-inline-block tw-h-2 tw-w-2 tw-rounded-full" :style="{ backgroundColor: item.color }" />
          <span class="tw-text-[12px] tw-text-[#4b5563]">{{ densityLabelText(item) }}</span>
        </div>
      </div>
    </template>
  </van-cell-group>
</template>

<style scoped>
.group-container {
  margin: 15px 0px;
  border: 2px solid #c8c9cc;
  border-radius: 20px;
}
</style>
